import { tryAssetIdLikeToLegacyAssetId } from "@repo/caip";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import {
  normalizeAssetDetailsResponse,
  normalizeAssetOverlayBatchResponse,
  normalizeBrowseAssetItems,
  normalizeStocksPageRows,
  type AssetDetailsOutput,
  type BrowseAssetItem,
  type NormalizedAssetDomainGraph,
  type StocksPageRow,
} from "../asset-domain-contract";
import type { AssetModel, AssetPriceModel, AssetProvider } from "./models";
import {
  normalizeAssetDomainAssetIds,
  toAssetDomainCollectionId,
  toAssetProviderExtensionId,
} from "./models";
import type {
  AssetDomainManagedCollections,
  AssetDomainSeedAssetPriceRow,
  AssetDomainSeedAssetRow,
  BrowserAssetDomainCollectionOptions,
} from "./collection-types";
import {
  getAssetDomainCollections,
  type AssetDomainCollections,
} from "./collection-registry";
import { syncCollectionRows } from "./collection-sync";

const KNOWN_ASSET_PROVIDERS: AssetProvider[] = ["ondo"];

export async function seedAssetDomainFromAssetDetails(
  assetId: string,
  response: AssetDetailsOutput,
  options?: BrowserAssetDomainCollectionOptions,
): Promise<string[]> {
  const normalizedAssetIds = normalizeAssetDomainAssetIds([assetId]);
  if (normalizedAssetIds.length === 0) {
    return [];
  }

  const normalized = normalizeAssetDetailsResponse({
    assetId: normalizedAssetIds[0]!,
    response,
    updatedAt: Date.now(),
  });
  await applyNormalizedAssetDomainToCollections({
    collections: getAssetDomainCollections({
      sessionScopeToken: options?.sessionScopeToken,
    }),
    managedCollections: {
      asset: true,
      assetEquityDetails: true,
      assetPrice: true,
      assetProviderExtensions: true,
      assetTaxonomy: true,
    },
    normalized,
    normalizedAssetIds,
  });

  return normalizedAssetIds;
}

/** @deprecated Use seedAssetDomainFromAssetDetails. */
export const seedAssetOverlaysFromAssetDetails =
  seedAssetDomainFromAssetDetails;

export async function seedAssetDomainFromBrowseAssets(
  items: readonly BrowseAssetItem[],
  options?: BrowserAssetDomainCollectionOptions,
): Promise<string[]> {
  const normalizedAssetIds = normalizeAssetDomainAssetIds(
    items.map((item) => item.assetId),
  );
  if (normalizedAssetIds.length === 0) {
    return [];
  }

  const normalized = normalizeBrowseAssetItems({
    items,
    updatedAt: Date.now(),
  });
  await applyNormalizedAssetDomainToCollections({
    collections: getAssetDomainCollections({
      sessionScopeToken: options?.sessionScopeToken,
    }),
    managedCollections: {
      asset: true,
      assetPrice: true,
      assetProviderExtensions: true,
      assetTaxonomy: true,
    },
    normalized,
    normalizedAssetIds,
  });

  return normalizedAssetIds;
}

export async function seedAssetDomainFromStocksPageRows(
  rows: readonly StocksPageRow[],
  options?: BrowserAssetDomainCollectionOptions,
): Promise<string[]> {
  const normalizedAssetIds = normalizeAssetDomainAssetIds(
    rows.map((row) => row.assetId),
  );
  if (normalizedAssetIds.length === 0) {
    return [];
  }

  const normalized = normalizeStocksPageRows({
    rows,
    updatedAt: Date.now(),
  });
  await applyNormalizedAssetDomainToCollections({
    collections: getAssetDomainCollections({
      sessionScopeToken: options?.sessionScopeToken,
    }),
    managedCollections: {
      asset: true,
      assetPrice: true,
      assetProviderExtensions: true,
    },
    normalized,
    normalizedAssetIds,
  });

  return normalizedAssetIds;
}

export async function seedAssetDomainFromPortfolioRows(
  input: {
    assetPrices: readonly AssetDomainSeedAssetPriceRow[];
    assets: readonly AssetDomainSeedAssetRow[];
  },
  options?: BrowserAssetDomainCollectionOptions,
): Promise<string[]> {
  const fallbackUpdatedAt = Date.now();
  const collections = getAssetDomainCollections({
    sessionScopeToken: options?.sessionScopeToken,
  });
  const managedAssetIds = new Set<string>();
  const assetRows: AssetModel[] = [];
  const assetPriceRows: AssetPriceModel[] = [];
  const currentAssets = input.assets.length > 0 ? collections.asset.state : undefined;
  const currentPrices = input.assetPrices.length > 0 ? collections.assetPrice.state : undefined;

  for (const asset of input.assets) {
    const parsed = normalizeAndParseSeedAssetId(asset.assetId);
    if (!parsed) {
      continue;
    }

    managedAssetIds.add(parsed.assetId);
    assetRows.push(
      mergeSeedAssetRow(currentAssets?.get(parsed.assetId), {
        address: parsed.address,
        assetId: parsed.assetId,
        chainId: parsed.chainId,
        decimals:
          typeof asset.decimals === "number" && Number.isFinite(asset.decimals)
            ? asset.decimals
            : null,
        id: toAssetDomainCollectionId(parsed.assetId),
        imageUrl: asset.iconUrl ?? null,
        name: asset.name,
        symbol: asset.symbol,
        updatedAt: fallbackUpdatedAt,
      }),
    );
  }

  for (const assetPrice of input.assetPrices) {
    const parsed = normalizeAndParseSeedAssetId(assetPrice.assetId);
    if (!parsed || !Number.isFinite(assetPrice.priceUsd)) {
      continue;
    }

    managedAssetIds.add(parsed.assetId);
    assetPriceRows.push(
      mergeSeedAssetPriceRow(currentPrices?.get(parsed.assetId), {
        assetId: parsed.assetId,
        buyCount1: coerceSeedMetric(assetPrice.buyCount1),
        change1Percent: coerceSeedMetric(assetPrice.change1Percent),
        change24Percent: coerceSeedMetric(assetPrice.priceChange24hPercent),
        circulatingMarketCap: coerceSeedMetric(assetPrice.circulatingMarketCap),
        high1Usd: coerceSeedMetric(assetPrice.high1Usd),
        id: toAssetDomainCollectionId(parsed.assetId),
        liquidity: coerceSeedMetric(assetPrice.liquidity),
        marketCap: coerceSeedMetric(assetPrice.marketCap),
        priceUsd: assetPrice.priceUsd,
        quoteToken: assetPrice.quoteToken ?? "USD",
        txnCount1: coerceSeedMetric(assetPrice.txnCount1),
        uniqueTransactions1: coerceSeedMetric(assetPrice.uniqueTransactions1),
        updatedAt:
          typeof assetPrice.updatedAt === "number" &&
          Number.isFinite(assetPrice.updatedAt)
            ? assetPrice.updatedAt
            : fallbackUpdatedAt,
        volume1: coerceSeedMetric(assetPrice.volume1),
        volume24: coerceSeedMetric(assetPrice.volume24),
      }),
    );
  }

  const normalizedAssetIds = [...managedAssetIds].sort((left, right) =>
    left.localeCompare(right),
  );
  if (normalizedAssetIds.length === 0) {
    return [];
  }

  await applyNormalizedAssetDomainToCollections({
    collections,
    managedCollections: {
      asset: true,
      assetPrice: true,
    },
    normalized: {
      asset: assetRows,
      assetEquityDetails: [],
      assetPrice: assetPriceRows,
      assetProviderExtensions: [],
      assetTaxonomy: [],
    },
    normalizedAssetIds,
  });

  return normalizedAssetIds;
}

function coerceSeedMetric(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function mergeSeedAssetRow(
  existing: AssetModel | undefined,
  incoming: AssetModel,
): AssetModel {
  if (!existing) {
    return incoming;
  }

  return {
    ...existing,
    ...incoming,
    address: nonEmptyString(incoming.address) ?? existing.address,
    chainId: incoming.chainId || existing.chainId,
    decimals: incoming.decimals ?? existing.decimals,
    imageUrl: incoming.imageUrl ?? existing.imageUrl,
    name: nonEmptyString(incoming.name) ?? existing.name,
    symbol: nonEmptyString(incoming.symbol) ?? existing.symbol,
    updatedAt: Math.max(existing.updatedAt, incoming.updatedAt),
  };
}

function nonEmptyString(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function mergeSeedAssetPriceRow(
  existing: AssetPriceModel | undefined,
  incoming: AssetPriceModel,
): AssetPriceModel {
  if (!existing) {
    return incoming;
  }

  const shouldApplyIncoming = incoming.updatedAt >= existing.updatedAt;
  if (!shouldApplyIncoming) {
    return existing;
  }

  return {
    ...existing,
    ...incoming,
    buyCount1: incoming.buyCount1 ?? existing.buyCount1,
    change1Percent: incoming.change1Percent ?? existing.change1Percent,
    change24Percent: incoming.change24Percent ?? existing.change24Percent,
    circulatingMarketCap:
      incoming.circulatingMarketCap ?? existing.circulatingMarketCap,
    high1Usd: incoming.high1Usd ?? existing.high1Usd,
    liquidity: incoming.liquidity ?? existing.liquidity,
    marketCap: incoming.marketCap ?? existing.marketCap,
    priceUsd: incoming.priceUsd ?? existing.priceUsd,
    quoteToken: incoming.quoteToken ?? existing.quoteToken,
    txnCount1: incoming.txnCount1 ?? existing.txnCount1,
    uniqueTransactions1:
      incoming.uniqueTransactions1 ?? existing.uniqueTransactions1,
    updatedAt: incoming.updatedAt,
    volume1: incoming.volume1 ?? existing.volume1,
    volume24: incoming.volume24 ?? existing.volume24,
  };
}

export function applyAssetOverlayBatchToCollections(input: {
  collections: AssetDomainCollections;
  normalizedAssetIds: readonly string[];
  response: Awaited<
    ReturnType<
      PlatformApiTrpcClient["assets"]["getAssetOverlaysBatch"]["query"]
    >
  >;
}) {
  const updatedAt = Date.now();
  const normalized = normalizeAssetOverlayBatchResponse({
    response: input.response,
    updatedAt,
  });

  // The overlay batch is the canonical writer for the asset collection: the
  // server merges base + custom override into a single effective row, so
  // we treat the response as authoritative — no per-field merge guard, and
  // missing rows clear the cached entry. This is the only way TanStack DB
  // tracks override clears (e.g. an admin unpublishing a custom row).
  return applyNormalizedAssetDomainToCollections({
    assetWriteMode: "authoritative",
    collections: input.collections,
    managedCollections: {
      asset: true,
      assetEquityDetails: true,
      assetProviderExtensions: true,
      assetTaxonomy: true,
    },
    normalized,
    normalizedAssetIds: input.normalizedAssetIds,
  });
}

function applyNormalizedAssetDomainToCollections(input: {
  assetWriteMode?: "authoritative" | "fill-only";
  collections: AssetDomainCollections;
  managedCollections: AssetDomainManagedCollections;
  normalized: NormalizedAssetDomainGraph;
  normalizedAssetIds: readonly string[];
}) {
  const tasks: Array<Promise<void>> = [];
  const assetWriteMode = input.assetWriteMode ?? "fill-only";

  if (input.managedCollections.asset) {
    const currentAssets = input.collections.asset.state;
    const assetRows =
      assetWriteMode === "authoritative"
        ? input.normalized.asset
        : input.normalized.asset.map((row) =>
            mergeSeedAssetRow(currentAssets.get(row.id), row),
          );
    tasks.push(
      syncCollectionRows(
        input.collections.asset,
        assetRows,
        input.normalizedAssetIds,
        { removeMissing: assetWriteMode === "authoritative" },
      ),
    );
  }

  if (input.managedCollections.assetPrice) {
    tasks.push(
      syncCollectionRows(
        input.collections.assetPrice,
        input.normalized.assetPrice,
        input.normalizedAssetIds,
        { removeMissing: false },
      ),
    );
  }

  if (input.managedCollections.assetTaxonomy) {
    tasks.push(
      syncCollectionRows(
        input.collections.assetTaxonomy,
        input.normalized.assetTaxonomy,
        input.normalizedAssetIds,
      ),
    );
  }

  if (input.managedCollections.assetEquityDetails) {
    tasks.push(
      syncCollectionRows(
        input.collections.assetEquityDetails,
        input.normalized.assetEquityDetails,
        input.normalizedAssetIds,
      ),
    );
  }

  if (input.managedCollections.assetProviderExtensions) {
    tasks.push(
      syncCollectionRows(
        input.collections.assetProviderExtensions,
        input.normalized.assetProviderExtensions,
        input.normalizedAssetIds.flatMap((assetId) =>
          KNOWN_ASSET_PROVIDERS.map((provider) =>
            toAssetProviderExtensionId(assetId, provider),
          ),
        ),
      ),
    );
  }

  return Promise.all(tasks);
}
function normalizeAndParseSeedAssetId(rawAssetId: string): {
  address: string;
  assetId: string;
  chainId: number;
} | null {
  const normalizedAssetId = normalizeAssetDomainAssetIds([
    tryAssetIdLikeToLegacyAssetId(rawAssetId) ?? rawAssetId,
  ])[0];
  if (!normalizedAssetId) {
    return null;
  }

  const separatorIndex = normalizedAssetId.lastIndexOf(":");
  if (separatorIndex <= 0 || separatorIndex >= normalizedAssetId.length - 1) {
    return null;
  }

  const address = normalizedAssetId.slice(0, separatorIndex);
  const chainId = Number.parseInt(
    normalizedAssetId.slice(separatorIndex + 1),
    10,
  );
  if (!Number.isFinite(chainId)) {
    return null;
  }

  return {
    address,
    assetId: normalizedAssetId,
    chainId,
  };
}
